import type { ChangeEvent, CSSProperties, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";
import styles from "./ds.module.css";

/** Bordered inline search with a focus ring. The placeholder shows a real example, never the label. */
export function SearchField({ value, onChange, onClear, placeholder = "Search…", width = 230, label = "Search", style }: {
    value: string;
    onChange: (value: string) => void;
    onClear?: () => void;
    placeholder?: string;
    width?: number | string;
    /** The accessible name; the field has no visible label. */
    label?: string;
    style?: CSSProperties;
}) {
    return (
        <div className={styles.search} style={{ width, maxWidth: "100%", ...style }}>
            <i className="bi bi-search" aria-hidden="true" />
            <input type="text" value={value} placeholder={placeholder} aria-label={label}
                onChange={(e) => onChange(e.target.value)} />
            {value && (
                <button type="button" className={styles.searchClear} aria-label="Clear search" onClick={() => (onClear ? onClear() : onChange(""))}>×</button>
            )}
        </div>
    );
}

export interface SelectOption { value: string; label: string }

/** A native select on the platform's control styling. `label` renders above; `aria-label` names an unlabelled one. */
export function Select({ label, required, options, placeholder, small = false, hint, style, className, ...rest }:
    Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> & {
        label?: ReactNode; options: SelectOption[]; placeholder?: string; small?: boolean; hint?: ReactNode;
    }) {
    const control = (
        <div className={styles.selectWrap}>
            <select className={`${styles.control} ${small ? styles.controlSm : ""} ${className ?? ""}`} {...rest}>
                {placeholder !== undefined && <option value="">{placeholder}</option>}
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span className={styles.caret} aria-hidden="true">▾</span>
        </div>
    );
    if (!label) return <div style={style}>{control}</div>;
    return (
        <label className={styles.field} style={style}>
            <span className={styles.fieldLabel}>{label}{required && <span className={styles.req}> *</span>}</span>
            {control}
            {hint && <span className={styles.fieldHint}>{hint}</span>}
        </label>
    );
}

export function Input({ label, required, small = false, hint, style, className, ...rest }:
    InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode; small?: boolean; hint?: ReactNode }) {
    const control = <input className={`${styles.control} ${small ? styles.controlSm : ""} ${className ?? ""}`} {...rest} />;
    if (!label) return <div style={style}>{control}</div>;
    return (
        <label className={styles.field} style={style}>
            <span className={styles.fieldLabel}>{label}{required && <span className={styles.req}> *</span>}</span>
            {control}
            {hint && <span className={styles.fieldHint}>{hint}</span>}
        </label>
    );
}

export type ChangeHandler<T extends HTMLElement> = (e: ChangeEvent<T>) => void;
